import {
  MAX_IN_FLIGHT_DOWNLOAD_PROGRESS,
  TRANSFER_TIME_LEFT_MIN_ELAPSED_MS,
} from "../constants/uploads";
import type {
  IEstimateTransferTimeLeftParams,
  IIntegrationFetchEntry,
  IResolvedTransferPopupCopy,
  IResolveTransferBytesParams,
  IResolveTransferPopupCopyParams,
  ISelectTransferPopupParams,
  IStampTransferStartTimesParams,
  ITransferBytes,
  ITransferPopupSelection,
  ITransferTimeLeft,
  TTransferPopupTitleI18nKey,
} from "../types/uploads";
import { EFileUploadStatus } from "../types/uploads";
import { formatUploadSizeLabel } from "./uploads";

const SECONDS_IN_MINUTE = 60;
const SECONDS_IN_HOUR = 3600;
const MILLISECONDS_IN_SECOND = 1000;
const FULL_PROGRESS = 100;
const SUBTITLE_SEPARATOR = " · ";

// NOTE: NSAI-7067 — the transfer popup is the one place a transfer is visible
// from anywhere in the app, so web and mobile must agree on every word of it.
// Everything below is the platform-agnostic half: which entry the card is
// about, what its headline says, and the "120 MB of 320 MB · 2 min left" line.

export const isTransferInFlight = (status: EFileUploadStatus): boolean =>
  status === EFileUploadStatus.FETCHING ||
  status === EFileUploadStatus.UPLOADING ||
  status === EFileUploadStatus.CONFIRMING;

const TITLE_KEY_BY_STATUS: Record<
  EFileUploadStatus,
  TTransferPopupTitleI18nKey
> = {
  [EFileUploadStatus.FETCHING]: "popup.title.fetching",
  [EFileUploadStatus.UPLOADING]: "popup.title.uploading",
  [EFileUploadStatus.CONFIRMING]: "popup.title.confirming",
  [EFileUploadStatus.DONE]: "popup.title.done",
  [EFileUploadStatus.ERROR]: "popup.title.error",
  // NOTE: A cancelled entry is removed from the list by its owner, so the popup
  // never actually renders one; the error copy is the honest fallback if it
  // ever does.
  [EFileUploadStatus.CANCELLED]: "popup.title.error",
};

export const resolveTransferPopupTitleKey = (
  status: EFileUploadStatus,
): TTransferPopupTitleI18nKey => TITLE_KEY_BY_STATUS[status];

export const resolveTransferPopupCopy = ({
  primary,
  inFlightCount,
  now,
  translate,
}: IResolveTransferPopupCopyParams): IResolvedTransferPopupCopy => {
  const isMultiple = inFlightCount > 1;
  const isPrimaryInFlight = isTransferInFlight(primary.status);

  if (isMultiple) {
    return {
      title: translate("popup.multiple", {
        count: inFlightCount,
      }),
      subtitle: translate("popup.multiple_hint", {
        fileName: primary.fileName,
      }),
      closeLabel: translate("popup.dismiss_aria"),
      isCloseCancelling: false,
    };
  }

  const title = translate(resolveTransferPopupTitleKey(primary.status), {
    fileName: primary.fileName,
  });

  const closeLabel = !isPrimaryInFlight
    ? translate("popup.dismiss_aria")
    : primary.status === EFileUploadStatus.FETCHING
      ? translate("progress.cancel_fetch_aria", {
          fileName: primary.fileName,
        })
      : translate("popup.cancel_aria", { fileName: primary.fileName });

  const resolveSubtitle = (): string | null => {
    switch (primary.status) {
      case EFileUploadStatus.DONE:
        return translate("popup.done_hint");
      case EFileUploadStatus.ERROR:
      case EFileUploadStatus.CANCELLED:
        return primary.errorMessage ?? translate("popup.error_hint");
      case EFileUploadStatus.CONFIRMING:
        return translate("table.processing_badge");
      default:
        break;
    }

    if (
      primary.status === EFileUploadStatus.FETCHING &&
      primary.progress >= FULL_PROGRESS
    ) {
      return translate("progress.preparing");
    }

    const bytes = resolveTransferBytes({ entry: primary });
    const timeLeft = estimateTransferTimeLeft({ entry: primary, now });
    const parts = [
      bytes &&
        translate("popup.bytes", {
          transferred: formatUploadSizeLabel({
            sizeInBytes: bytes.transferredBytes,
          }),
          total: formatUploadSizeLabel({ sizeInBytes: bytes.totalBytes }),
        }),
      timeLeft && translate(timeLeft.i18nKey, { count: timeLeft.count }),
    ].filter((part): part is string => Boolean(part));

    if (parts.length > 0) {
      return parts.join(SUBTITLE_SEPARATOR);
    }

    if (primary.status === EFileUploadStatus.FETCHING) {
      return primary.sourceName
        ? translate("steps.fetching_from", { source: primary.sourceName })
        : translate("progress.fetching");
    }

    return null;
  };

  return {
    title,
    subtitle: resolveSubtitle(),
    closeLabel,
    isCloseCancelling: isPrimaryInFlight,
  };
};

// NOTE: How many bytes have moved and how many there are in all, or null when
// the total is unknowable. An upload knows its file's size up front. A
// connector fetch only knows it through the provider's Content-Length, which
// is carried beside the rounded percentage. Reconstructing the total from that
// percentage makes it visibly change as the download advances, so a stream
// without an exact total yields null rather than a made-up denominator.
export const resolveTransferBytes = ({
  entry,
}: IResolveTransferBytesParams): ITransferBytes | null => {
  if (entry.status === EFileUploadStatus.FETCHING) {
    const downloadedBytes = entry.downloadedBytes ?? 0;
    if (
      entry.progress <= 0 ||
      entry.progress > MAX_IN_FLIGHT_DOWNLOAD_PROGRESS ||
      downloadedBytes <= 0 ||
      entry.totalBytes === undefined ||
      entry.totalBytes <= 0
    ) {
      return null;
    }

    return {
      transferredBytes: downloadedBytes,
      totalBytes: entry.totalBytes,
    };
  }

  if (entry.totalBytes === undefined || entry.totalBytes <= 0) {
    return null;
  }

  const clampedProgress = Math.min(Math.max(entry.progress, 0), FULL_PROGRESS);

  return {
    transferredBytes: Math.round((entry.totalBytes * clampedProgress) / 100),
    totalBytes: entry.totalBytes,
  };
};

// NOTE: Extrapolates the average rate since the transfer began. A smoothed
// instantaneous rate would react faster, but it needs a sample history per
// entry that one side would have to keep in a ref and the other in state — the
// average needs only `startedAt`, which the entry already carries, so both
// clients compute the identical estimate from the identical inputs. Null while
// the estimate would be noise: before TRANSFER_TIME_LEFT_MIN_ELAPSED_MS, before
// any progress, and in the phases that have no percentage to extrapolate from
// (confirming, a fetch at "preparing").
export const estimateTransferTimeLeft = ({
  entry,
  now,
}: IEstimateTransferTimeLeftParams): ITransferTimeLeft | null => {
  if (
    entry.startedAt === undefined ||
    (entry.status !== EFileUploadStatus.UPLOADING &&
      entry.status !== EFileUploadStatus.FETCHING) ||
    entry.progress <= 0 ||
    entry.progress >= FULL_PROGRESS
  ) {
    return null;
  }

  const elapsedMs = now - entry.startedAt;
  if (elapsedMs < TRANSFER_TIME_LEFT_MIN_ELAPSED_MS) {
    return null;
  }

  const remainingSeconds = Math.ceil(
    (elapsedMs * (FULL_PROGRESS - entry.progress)) /
      entry.progress /
      MILLISECONDS_IN_SECOND,
  );

  if (remainingSeconds < SECONDS_IN_MINUTE) {
    return {
      i18nKey: "popup.time_left_seconds",
      count: Math.max(remainingSeconds, 1),
    };
  }

  if (remainingSeconds < SECONDS_IN_HOUR) {
    return {
      i18nKey: "popup.time_left_minutes",
      count: Math.ceil(remainingSeconds / SECONDS_IN_MINUTE),
    };
  }

  return {
    i18nKey: "popup.time_left_hours",
    count: Math.ceil(remainingSeconds / SECONDS_IN_HOUR),
  };
};

// NOTE: Which entry the card headlines and what the multi-file summary counts.
// The order is "what the user is still waiting on" first: something moving
// beats a failure, and a failure beats a success — a success that pushed a
// still-running upload off the card would read as "all done" while bytes were
// still moving.
export const selectTransferPopupEntries = ({
  transfers,
  hiddenIds,
}: ISelectTransferPopupParams): ITransferPopupSelection => {
  const visible = transfers.filter((entry) => !hiddenIds.has(entry.id));
  const inFlight = visible.filter((entry) => isTransferInFlight(entry.status));

  const primary =
    inFlight[0] ??
    visible.find((entry) => entry.status === EFileUploadStatus.ERROR) ??
    visible.find((entry) => entry.status === EFileUploadStatus.DONE) ??
    null;

  const aggregateProgress =
    inFlight.length === 0
      ? 0
      : Math.round(
          inFlight.reduce((sum, entry) => sum + entry.progress, 0) /
            inFlight.length,
        );

  return {
    primary,
    visibleCount: visible.length,
    inFlightCount: inFlight.length,
    aggregateProgress,
  };
};

// NOTE: An upload records its own `startedAt` when it begins, but a connector
// fetch is rebuilt from the attachment list on every progress tick and has no
// memory of when it started — so the provider that owns the fetch list stamps
// it, keeping the first-seen time per id. Without it the popup could not print
// "time left" for the phase that, for a large video, is the longest wait.
export const stampTransferStartTimes = ({
  entries,
  startTimes,
  now,
}: IStampTransferStartTimesParams): IIntegrationFetchEntry[] => {
  const currentIds = new Set(entries.map((entry) => entry.id));
  for (const knownId of startTimes.keys()) {
    if (!currentIds.has(knownId)) {
      startTimes.delete(knownId);
    }
  }

  return entries.map((entry) => {
    const knownStart = startTimes.get(entry.id);
    if (knownStart !== undefined) {
      return { ...entry, startedAt: knownStart };
    }

    startTimes.set(entry.id, now);
    return { ...entry, startedAt: now };
  });
};
